import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from './ui/card';
import { CalendarClock, Briefcase } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

const dateLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
};

const fmtTime = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

export default function MyScheduleCard({ days = 7 }) {
  const { token } = useAuth();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    axios
      .get(`${API}/my/schedule?days=${days}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (alive) setItems(data.schedule || []); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [token, days]);

  if (!items || items.length === 0) return null;

  const byDate = items.reduce((acc, it) => {
    (acc[it.date] = acc[it.date] || []).push(it);
    return acc;
  }, {});

  return (
    <Card className="p-4 mb-4 border border-blue-200 dark:border-[#2D3748] bg-blue-50/50 dark:bg-[#1A2433]" data-testid="my-schedule-card">
      <h2 className="font-semibold flex items-center gap-2 mb-3 text-gray-900 dark:text-gray-100">
        <CalendarClock className="w-5 h-5 text-blue-500" />
        My Schedule — next {days} days
      </h2>
      <div className="space-y-3">
        {Object.keys(byDate).sort().map((date) => (
          <div key={date}>
            <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">{dateLabel(date)}</p>
            <div className="space-y-1">
              {byDate[date].map((s, i) => (
                <div
                  key={`${date}-${i}`}
                  className="flex items-center justify-between p-2 rounded-lg bg-white dark:bg-[#0F1923] border border-gray-200 dark:border-[#2D3748]"
                  data-testid={`my-schedule-item-${date}-${i}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {s.title || 'Shift'}
                      {s.team_name && <span className="text-slate-500 font-normal"> · {s.team_name}</span>}
                    </p>
                    {s.notes && <p className="text-xs text-slate-500 truncate">{s.notes}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    {s.job_id && <Briefcase className="w-3.5 h-3.5 text-slate-400" />}
                    <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                      {fmtTime(s.start_time)}–{fmtTime(s.end_time)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
